import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LockKeyhole } from 'lucide-react';
import { Button } from '@cartcloser/ui';
import { fetchMe, type Me } from '../lib/api.js';
import { AppShell, type NavItem, type NavSection } from './app-shell.js';

export type { NavItem, NavSection };

/** Grouped nav for a store dashboard. Every item sits under a heading. */
export function storeNav(slug: string): NavSection[] {
  return [
    {
      heading: 'Overview',
      items: [
        { label: 'Dashboard', to: `/admin/${slug}`, exact: true },
        { label: 'Analytics', to: `/admin/${slug}/analytics` },
        { label: 'Conversations', to: `/admin/${slug}/conversations` },
      ],
    },
    {
      heading: 'Agents',
      items: [
        { label: 'Configure', to: `/admin/${slug}/agents` },
        { label: 'Sandbox', to: `/admin/${slug}/sandbox` },
      ],
    },
    {
      heading: 'Products',
      items: [
        { label: 'Catalogue', to: `/admin/${slug}/catalog` },
        { label: 'SKU mapping', to: `/admin/${slug}/mapping` },
      ],
    },
    {
      heading: 'Workspace',
      items: [
        { label: 'Team', to: `/admin/${slug}/members` },
        { label: 'Settings', to: `/admin/${slug}/settings` },
        { label: 'What’s new', to: `/admin/${slug}/whats-new` },
      ],
    },
  ];
}

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: fetchMe });
}

export function DashboardLayout({ slug, children }: { slug: string; children: ReactNode }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMe();

  // Only a confirmed "not signed in" (null) sends the user to sign-in; a
  // transient fetch error shows a retry state instead of logging them out.
  useEffect(() => {
    if (!isLoading && !isError && data === null) void navigate({ to: '/signin' });
  }, [data, isLoading, isError, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-muted-foreground">
        <p>Couldn’t load your account. Check your connection and try again.</p>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!data) return null;

  const membership = data.memberships.find((m) => m.slug === slug);
  if (!membership) return <NoAccess me={data} />;

  return (
    <AppShell
      brandSub={membership.name}
      nav={storeNav(slug)}
      email={data.user.email}
      headerLabel={`/${slug}`}
      headerExtra={
        data.user.isSuperAdmin ? (
          <Link
            to="/super/templates"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <LockKeyhole className="size-3.5" aria-hidden="true" />
            Platform
          </Link>
        ) : null
      }
    >
      {children}
    </AppShell>
  );
}

function NoAccess({ me }: { me: Me }) {
  const first = me.memberships[0];
  return (
    <main className="mx-auto max-w-md px-6 py-20 text-center">
      <h1 className="text-xl font-semibold">No access to this store</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Your account isn’t a member of this store.
      </p>
      <div className="mt-6">
        {first ? (
          <Button asChild>
            <Link to="/admin/$slug" params={{ slug: first.slug }}>
              Go to {first.name}
            </Link>
          </Button>
        ) : (
          <Button asChild>
            <Link to="/onboarding">Create a store</Link>
          </Button>
        )}
      </div>
    </main>
  );
}
